/**
 * Bouton flottant (bas droite) pour entrer / quitter le mode personnalisation.
 * En mode édition : deux boutons icônes (+ ajouter un widget, ✓ valider).
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useGridStackContext } from "../../lib";
import { autoArrangeGrid } from "./dashboardAutoArrange";
import { useDashboardEdit } from "./DashboardEditContext";
import { useDashboardSave } from "./DashboardSave";

export function DashboardCustomizeFab() {
  const navigate = useNavigate();
  const { isEditing, enterEditMode, exitEditMode } = useDashboardEdit();
  const { flushNow, requestSave } = useDashboardSave();
  const { gridStack, _rawWidgetMetaMap } = useGridStackContext();

  // Range le dashboard d'un coup : regroupe par type et comble les trous.
  const handleAutoArrange = () => {
    if (!gridStack) return;
    autoArrangeGrid(gridStack, _rawWidgetMetaMap.value);
    // Le 'change' GridStack relance déjà l'autosave ; on le demande explicitement
    // au cas où aucune position ne bouge réellement.
    requestSave();
  };

  // À la sortie du mode édition : on force l'envoi immédiat d'une éventuelle
  // modification en attente (l'autosave gère le reste en continu), puis on quitte.
  const handleFinish = async () => {
    await flushNow();
    exitEditMode();
  };

  if (isEditing) {
    return (
      // z-index inline (60) : au-dessus des widgets en resize (z-50) et des poignées.
      <div
        className="fixed bottom-6 right-6 flex flex-col items-end gap-3"
        style={{ zIndex: 60 }}
      >
        <button
          type="button"
          id="dashboard-auto-arrange-fab"
          onClick={handleAutoArrange}
          className="w-12 h-12 rounded-xl bg-white text-tertiary border border-grey-light shadow-lg hover:bg-grey-light/60 transition-colors flex items-center justify-center"
          aria-label="Ranger automatiquement (regrouper par type, combler les trous)"
          title="Ranger : regrouper par type et combler les trous"
        >
          <FontAwesomeIcon icon="broom" className="text-lg" />
        </button>
        <button
          type="button"
          id="dashboard-add-widget-fab"
          onClick={() => navigate("/widgets")}
          className="w-12 h-12 rounded-xl bg-secondary text-tertiary shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center"
          aria-label="Ajouter un widget"
          title="Ajouter un widget"
        >
          <FontAwesomeIcon icon="plus" className="text-lg" />
        </button>
        <button
          type="button"
          onClick={handleFinish}
          className="w-14 h-14 rounded-xl bg-tertiary text-secondary-text shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center"
          aria-label="Valider la personnalisation"
          title="Valider"
        >
          <FontAwesomeIcon icon="check" className="text-xl" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      id="dashboard-edit-fab"
      onClick={enterEditMode}
      className="fixed bottom-6 right-6 w-14 h-14 rounded-xl bg-tertiary text-secondary-text shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center"
      style={{ zIndex: 60 }}
      aria-label="Personnaliser le dashboard"
      title="Personnaliser le dashboard"
    >
      <FontAwesomeIcon icon="pen" className="text-xl" />
    </button>
  );
}
